import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

/**
 * Desktop breakpoint for the app. Kept as a single exported constant so that
 * the media query string used here is guaranteed to match the CSS `when`
 * attribute on the chat split-pane (`chat-container.page.html`). If these two
 * ever drift apart, the layout and the navigation logic can disagree and a
 * mobile user can be served the desktop strategy (or vice versa).
 */
export const DESKTOP_BREAKPOINT_PX = 1096;
export const DESKTOP_MEDIA_QUERY = `(min-width: ${DESKTOP_BREAKPOINT_PX}px)`;

/**
 * ViewportService
 *
 * Single source of truth for "are we on desktop?" across the whole app.
 *
 * Why this exists:
 * - The chat experience is desktop (split-pane) vs mobile (two separate views).
 *   Previously this decision was computed in ~6 places using two different
 *   APIs — `window.matchMedia('(min-width: 1096px)')` AND `platform.width() >= 1096`.
 *   Those two signals are not the same and do not update at the same time, so
 *   they could disagree at a single moment (e.g. no back button because JS
 *   thought desktop, no sidebar because CSS knew it was mobile → user trapped).
 * - Several call sites latched `isDesktop` once in a constructor from
 *   `platform.width()`, which returns a stale/unsettled value during a cold
 *   start launched from a push-notification deep link, and only corrected on a
 *   later resize event that may never fire.
 *
 * This service uses `window.matchMedia` — the exact same mechanism the
 * split-pane's `when` attribute uses — so the layout and the logic can never
 * disagree, the value is correct even at cold start, and it stays reactive when
 * a desktop window is shrunk down to mobile.
 */
@Injectable({
	providedIn: 'root',
})
export class ViewportService {
	private mediaQuery: MediaQueryList | null = null;
	private _isDesktop: BehaviorSubject<boolean>;

	/** Emits whenever the desktop/mobile state changes (deduplicated). */
	public readonly isDesktop$: Observable<boolean>;

	constructor() {
		const initial = this.evaluate();
		this._isDesktop = new BehaviorSubject<boolean>(initial);
		this.isDesktop$ = this._isDesktop.asObservable().pipe(distinctUntilChanged());

		if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
			this.mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
			// addEventListener('change') is supported on all target WebViews; the
			// deprecated addListener fallback covers very old runtimes.
			const handler = (e: MediaQueryListEvent) => this._isDesktop.next(e.matches);
			if (typeof this.mediaQuery.addEventListener === 'function') {
				this.mediaQuery.addEventListener('change', handler);
			} else if (typeof (this.mediaQuery as any).addListener === 'function') {
				(this.mediaQuery as any).addListener(handler);
			}
		}
	}

	/** Synchronous current value. Prefer `isDesktop$` where reactivity matters. */
	public get isDesktop(): boolean {
		return this._isDesktop.value;
	}

	/** Convenience inverse of `isDesktop`. */
	public get isMobile(): boolean {
		return !this._isDesktop.value;
	}

	private evaluate(): boolean {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
			return false;
		}
		return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
	}
}
